import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Loader2, Heart } from 'lucide-react';
import { UsageMeter } from '@/components/UsageMeter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: 'romance', label: 'Romance' },
  { value: 'scifi', label: 'Sci-Fi' },
  { value: 'mystery', label: 'Mystery' },
  { value: 'horror', label: 'Horror' },
  { value: 'fantasy', label: 'Fantasy' },
  { value: 'drama', label: 'Drama' },
];

const styles = [
  { value: 'poetic', label: 'Poetic' },
  { value: 'dramatic', label: 'Dramatic' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'surreal', label: 'Surreal' },
  { value: 'noir', label: 'Noir' },
  { value: 'one-word', label: 'One Word' },
];

const PromptGenerator = ({ onUsePrompt }) => {
  const [category, setCategory] = useState('romance');
  const [style, setStyle] = useState('poetic');
  const [customTheme, setCustomTheme] = useState('');
  const [prompts, setPrompts] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [favoritedIndexes, setFavoritedIndexes] = useState(new Set());
  const [favoritingIndex, setFavoritingIndex] = useState(null);
  const [usage, setUsage] = useState(null);
  const [quotaHit, setQuotaHit] = useState(false);

  const fetchUsage = useCallback(
    () => axios.get(`${API}/usage`, { withCredentials: true }).then(({ data }) => setUsage(data)).catch((e) => console.warn('Usage fetch failed', e)),
    []
  );

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const handleFavorite = async (prompt, index) => {
    setFavoritingIndex(index);
    try {
      await axios.post(
        `${API}/favorites`,
        { prompt, category, style },
        { withCredentials: true }
      );
      setFavoritedIndexes((prev) => new Set(prev).add(index));
    } catch (err) {
      setError('Could not save favorite');
    } finally {
      setFavoritingIndex(null);
    }
  };

  const generatePrompts = async () => {
    setLoading(true);
    setError('');
    setQuotaHit(false);
    setPrompts('');
    setFavoritedIndexes(new Set());

    try {
      const response = await fetch(`${API}/prompts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          category,
          style,
          custom_theme: customTheme || null,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        if (response.status === 429 || response.status === 403) setQuotaHit(true);
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Failed to generate prompts');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        // SSE events are separated by \n\n
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          if (!event.startsWith('data: ')) continue;
          const jsonStr = event.slice(6);
          try {
            const parsed = JSON.parse(jsonStr);
            if (parsed.done) {
              break;
            }
            if (parsed.error) {
              setError(parsed.error);
              break;
            }
            if (parsed.content) {
              accumulated += parsed.content;
              setPrompts(accumulated);
            }
          } catch (e) {
            console.warn('Skipping malformed stream event', e);
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to generate prompts');
    } finally {
      setLoading(false);
      fetchUsage();
    }
  };

  const parsePrompts = (text) => {
    if (!text) return [];
    const lines = text.split('\n').filter((line) => line.trim());
    const promptList = [];
    
    for (const line of lines) {
      const match = line.match(/^\d+\.\s*(.+)$/);
      if (match) {
        promptList.push(match[1].trim());
      }
    }
    
    return promptList.length > 0 ? promptList : (text.trim() ? [text.trim()] : []);
  };

  const promptList = parsePrompts(prompts);

  return (
    <div className="space-y-6" data-testid="prompt-generator">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="category">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger id="category" data-testid="category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="style">Style</Label>
          <Select value={style} onValueChange={setStyle}>
            <SelectTrigger id="style" data-testid="style-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {styles.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="theme">Custom Theme (Optional)</Label>
          <Input
            id="theme"
            placeholder="e.g., autumn, first love"
            value={customTheme}
            onChange={(e) => setCustomTheme(e.target.value)}
            data-testid="custom-theme-input"
          />
        </div>
      </div>

      <Button
        onClick={generatePrompts}
        disabled={loading}
        className="w-full bg-purple-600 hover:bg-purple-700"
        data-testid="generate-prompts-button"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="mr-2 h-4 w-4" />
            Generate Prompts
          </>
        )}
      </Button>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex flex-wrap items-center justify-between gap-2" data-testid="prompt-error">
          <span>{error}</span>
          {quotaHit && usage?.plan !== 'writer' && (
            <Link to="/pricing" className="font-semibold underline underline-offset-2 hover:text-red-900" data-testid="prompt-error-upgrade-link">See Writer plan</Link>
          )}
        </div>
      )}

      <UsageMeter usage={usage} />

      {promptList.length > 0 && (
        <div className="space-y-4" data-testid="prompts-list">
          <h3 className="text-lg font-semibold text-gray-900">Generated Prompts</h3>
          <div className="grid gap-4">
            {promptList.map((prompt, index) => (
              <Card key={prompt} className="hover:shadow-md transition-shadow" data-testid={`prompt-card-${index}`}>
                <CardContent className="p-4">
                  <p className="text-gray-700 mb-3 italic">&ldquo;{prompt}&rdquo;</p>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      onClick={() => onUsePrompt(prompt)}
                      className="bg-amber-900 hover:bg-amber-800 text-amber-50"
                      data-testid={`use-prompt-button-${index}`}
                    >
                      Use This Prompt
                    </Button>
                    <Button
                      size="sm"
                      variant={favoritedIndexes.has(index) ? 'default' : 'outline'}
                      onClick={() => handleFavorite(prompt, index)}
                      disabled={favoritingIndex === index}
                      className={favoritedIndexes.has(index) ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'border-rose-300 text-rose-600 hover:bg-rose-50'}
                      data-testid={`favorite-prompt-button-${index}`}
                    >
                      <Heart className={`h-4 w-4 ${favoritedIndexes.has(index) ? 'fill-current' : ''}`} />
                      <span className="ml-1">{favoritedIndexes.has(index) ? 'Saved' : 'Favorite'}</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptGenerator;
